import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { ArrowRightIcon } from '@navikt/aksel-icons';
import type { WorkEnvironmentEvaluation } from '../../../api/workEnvironmentTypes';
import { focusResults, readingKey } from '../evaluationView';
import { LEVEL_COLORS, oneDecimal } from '../riskView';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from './Evaluation.module.css';

/** How the areas of the plans did, before and after the measures */
export const FocusResultCard = ({
  data,
}: {
  data: WorkEnvironmentEvaluation;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const level = (value: string) =>
    t(`workEnvironmentRisk.matrix.levels.${value}`);
  return (
    <section
      aria-labelledby="evaluation-focus-heading"
      className={process.card}
    >
      <div className={process.cardBody}>
        <span
          className={`ds-label ${process.eyebrow}`}
          data-size="sm"
          id="evaluation-focus-heading"
        >
          {t('workEnvironmentEvaluation.result.focus.eyebrow')}
        </span>
        {focusResults(data).map(
          ({ key, name, goal, measures, done, result }) => (
            <div key={key} className={classes.focus}>
              <Heading level={2} data-size="sm">
                {t(
                  `workEnvironmentEvaluation.result.focus.${result?.change ?? 'none'}`,
                  { name },
                )}
              </Heading>
              {result?.after && (
                <div className={classes.change}>
                  <Tag
                    data-color={LEVEL_COLORS[result.before.level]}
                    data-size="sm"
                  >
                    {t('workEnvironmentEvaluation.result.focus.before', {
                      level: level(result.before.level),
                    })}
                  </Tag>
                  <ArrowRightIcon aria-hidden />
                  <Tag
                    data-color={LEVEL_COLORS[result.after.level]}
                    data-size="sm"
                  >
                    {t('workEnvironmentEvaluation.result.focus.now', {
                      level: level(result.after.level),
                    })}
                  </Tag>
                  <span className="ds-paragraph" data-size="sm">
                    {t(
                      `workEnvironmentEvaluation.result.focus.mean.${result.dimension}`,
                      {
                        before: oneDecimal(result.before.score, i18n.language),
                        after: oneDecimal(result.after.score, i18n.language),
                      },
                    )}
                  </span>
                  <span className="ds-paragraph" data-size="sm">
                    {t('workEnvironmentEvaluation.result.focus.likelihood', {
                      before: result.before.likelihood,
                      after: result.after.likelihood,
                    })}
                  </span>
                </div>
              )}
              {result && readingKey(result) && (
                <Paragraph data-size="sm" className={process.subtle}>
                  {t(readingKey(result)!)}
                </Paragraph>
              )}
              <Paragraph data-size="sm">
                {[
                  t('workEnvironmentEvaluation.result.focus.measures', {
                    done,
                    count: measures,
                  }),
                  goal &&
                    t('workEnvironmentEvaluation.result.focus.goal', { goal }),
                ]
                  .filter(Boolean)
                  .join(' ')}
              </Paragraph>
            </div>
          ),
        )}
      </div>
    </section>
  );
};
